export type ErrorCode = "CONFLICT" | "AUTH_INVALID_CREDENTIALS" | string;

interface ErrorResponse {
  success: false;
  error: {
    code: ErrorCode;
    message: string;
  };
}

// Global error messages for unhandled errors
const globalErrorMessages: Record<ErrorCode, string> = {
  CONFLICT: "error.global.CONFLICT",
  AUTH_INVALID_CREDENTIALS: "error.global.AUTH_INVALID_CREDENTIALS",
};

// Specific error messages for known error cases
export const specificErrorMessages: Record<string, Record<ErrorCode, string>> = {
  createTruck: {
    CONFLICT: "error.createTruck.CONFLICT",
  },
  createUser: {
    CONFLICT: "error.createUser.CONFLICT",
  },
};

type TranslationFunction = (key: string, options?: { [key: string]: string }) => string;

export const getErrorMessage = (error: any, t: TranslationFunction, context?: string): string => {
  // Check for error code in the response data
  if (error?.data?.error?.code) {
    const errorCode = error.data.error.code;

    // First try to get a specific error message for the context
    if (context && specificErrorMessages[context]?.[errorCode]) {
      return t(specificErrorMessages[context][errorCode]);
    }

    // If no specific message, use the global message
    const globalMessage = globalErrorMessages[errorCode];
    if (globalMessage) {
      return t(globalMessage);
    }

    // Fallback to the server's error message if no custom message exists
    return t("error.global.UNKNOWN", { code: errorCode });
  }

  // Check for error code in the response data (alternative structure)
  if (error?.response?.data?.error?.code) {
    const errorCode = error.response.data.error.code;

    // First try to get a specific error message for the context
    if (context && specificErrorMessages[context]?.[errorCode]) {
      return t(specificErrorMessages[context][errorCode]);
    }

    // If no specific message, use the global message
    const globalMessage = globalErrorMessages[errorCode];
    if (globalMessage) {
      return t(globalMessage);
    }

    // Fallback to the server's error message if no custom message exists
    return t("error.global.UNKNOWN", { code: errorCode });
  }

  // Fallback to a generic error message
  return t("error.global.DEFAULT");
};

export const isErrorResponse = (error: any): error is ErrorResponse => {
  return (
    (error?.data?.error?.code && error?.data?.error?.message) ||
    (error?.response?.data?.error?.code && error?.response?.data?.error?.message)
  );
};
